import { useEffect, useState } from "react";
import {
  fetchProjects,
  downloadProjectsExcel,
  fetchProjectDetails,
  updateProject,
  fetchProjectsByOrg,
} from "../Api/ViewProjects";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const ViewProjects = () => {
  const [projects, setProjects] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const navigate = useNavigate();

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId; // For API call
  const orgId = userDetails?.organisationId; // For API call

  const [projectData, setProjectData] = useState({
    prTitle: "",
    prDescription: "",
    prTags: "",
    prStartDate: "",
    prEndDate: "",
    prClient: "",
    prTypes: "",
    teamAssigned: "",
    projectStatus: "",
    projectCategory: "",
    comments: "",
    salespersonCode: "",
    currentProjectManager: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const data = await fetchProjectsByOrg(orgId);
        console.log("Fetched Projects:", data);
        setProjects(data || []);
      } catch (error) {
        toast.error(`🦄 Error Fetching Projects`);
      }
    };

    loadProjects();
  }, []);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Create Excel file from current projects data (what's showing on screen)
      const workbook = new (await import("xlsx")).utils.book_new();
      const worksheetData = [
        // Headers
        [
          "Project ID",
          "Title",
          "Description",
          "Tags",
          "Start Date",
          "End Date",
          "Client",
          "Type",
          "User ID",
          "Team Assigned",
          "Status",
          "Category",
          "Comments",
          "Created On Date",
          "Created On Time",
          "Created By",
          "Salesperson Code",
          "Project Manager",
          // "Prozma Project ID",
        ],
        // Data rows
        ...projects.map((project) => [
          project.projectId || "",
          project.prTitle || "",
          project.prDescription || "",
          project.prTags || "",
          project.prStartDate
            ? moment(project.prStartDate).format("YYYY-MM-DD")
            : "",
          project.prEndDate
            ? moment(project.prEndDate).format("YYYY-MM-DD")
            : "",
          project.prClient || "",
          project.prTypes || "",
          project.usersId || "",
          project.teamAssigned || "",
          project.projectStatus || "",
          project.projectCategory || "",
          project.comments || "",
          project.createdOnDate
            ? moment(project.createdOnDate).format("YYYY-MM-DD")
            : "",
          project.createdOnTime || "",
          project.createdBy || "",
          project.salespersonCode || "",
          project.currentProjectManager || "",
          // project.prozmaPrId || "",
        ]),
      ];

      const worksheet = (await import("xlsx")).utils.aoa_to_sheet(
        worksheetData
      );
      (await import("xlsx")).utils.book_append_sheet(
        workbook,
        worksheet,
        "Projects"
      );

      // Generate and download the file
      (await import("xlsx")).writeFile(
        workbook,
        `Projects_${orgId}_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
      );

      toast.success("Projects data download started!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`🦄 Download failed: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedProjectId("");
    setProjectData({
      prTitle: "",
      prDescription: "",
      prTags: "",
      prStartDate: "",
      prEndDate: "",
      prClient: "",
      prTypes: "",
      teamAssigned: "",
      projectStatus: "",
      projectCategory: "",
      comments: "",
      salespersonCode: "",
      currentProjectManager: "",
    });
  };

  const handleProjectSelect = async (projectId) => {
    setSelectedProjectId(projectId);
    try {
      setIsLoading(true);
      const data = await fetchProjectDetails(projectId);
      setProjectData({
        prTitle: data.prTitle || "",
        prDescription: data.prDescription || "",
        prTags: data.prTags || "",
        prStartDate: data.prStartDate
          ? moment(data.prStartDate).format("YYYY-MM-DD")
          : "",
        prEndDate: data.prEndDate
          ? moment(data.prEndDate).format("YYYY-MM-DD")
          : "",
        prClient: data.prClient || "",
        prTypes: data.prTypes || "",
        teamAssigned: data.teamAssigned || "",
        projectStatus: data.projectStatus || "",
        projectCategory: data.projectCategory || "",
        comments: data.comments || "",
        salespersonCode: data.salespersonCode || "",
        currentProjectManager: data.currentProjectManager || "",
      });
    } catch (error) {
      toast.error(`🦄 Error fetching project details`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProjectData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProjectId) {
      toast.error("Please select a project");
      return;
    }

    try {
      setIsLoading(true);
      await updateProject(selectedProjectId, projectData);
      toast.success("Project updated successfully!");

      // Refresh the projects list
      const data = await fetchProjectsByOrg(orgId);
      setProjects(data || []);

      handleCloseEditModal();
    } catch (error) {
      toast.error(`🦄 Error updating project: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="main-content expand">
        <Container fluid className="list-container">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="list-title">View Projects</h2>
            <div>
              {/* <Button
                variant="secondary"
                onClick={handleEditClick}
                disabled={projects.length === 0}
                className="me-2"
              >
                Edit Projects
              </Button> */}
              <Button
                className="generate-btn"
                variant="primary"
                onClick={handleDownload}
                disabled={isDownloading || projects.length === 0}
              >
                {isDownloading ? "Downloading..." : "Download Data"}
              </Button>
            </div>
          </div>

          {projects.length > 0 ? (
            <div className="table-wrapper">
              <div className="table-scroll-container">
                <Table bordered hover responsive className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Project ID</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Tags</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Client</th>
                      <th>Type</th>
                      <th>User ID</th>
                      <th>Team</th>
                      <th>Status</th>
                      <th>Category</th>
                      <th>Comments</th>
                      <th>Created On</th>
                      <th>Created By</th>
                      <th>Changed On</th>
                      <th>Changed By</th>
                      <th>Salesperson Code</th>
                      <th>Project Manager</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        {/* <td>{project.projectId}</td> */}
                        <td
                          style={{ cursor: "pointer", color: "#007bff" }}
                          onClick={() =>
                            navigate(`/project/${project.projectId}`)
                          }
                        >
                          {project.projectId}
                        </td>
                        <td>{project.prTitle}</td>
                        <td>{project.prDescription}</td>
                        <td>{project.prTags}</td>
                        <td>
                          {project.prStartDate
                            ? moment(project.prStartDate).format("L")
                            : "N/A"}
                        </td>
                        <td>
                          {project.prEndDate
                            ? moment(project.prEndDate).format("L")
                            : "N/A"}
                        </td>
                        <td>{project.prClient}</td>
                        <td>{project.prTypes}</td>
                        <td>{project.usersId}</td>
                        <td>{project.teamAssigned}</td>
                        <td>{project.projectStatus}</td>
                        <td>{project.projectCategory}</td>
                        <td>{project.comments}</td>
                        <td>
                          {project.createdOnDate
                            ? moment(project.createdOnDate).format("L")
                            : "N/A"}
                          {project.createdOnTime &&
                            ` at ${project.createdOnTime.split(".")[0]}`}
                        </td>
                        <td>{project.createdBy || "N/A"}</td>
                        <td>
                          {project.changedOnDate
                            ? moment(project.changedOnDate).format("L")
                            : "N/A"}
                          {project.changedOnTime &&
                            ` at ${project.changedOnTime.split(".")[0]}`}
                        </td>
                        <td>{project.changedBy}</td>
                        <td>{project.salespersonCode}</td>
                        <td>{project.currentProjectManager}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            <p className="no-data">No projects found.</p>
          )}
        </Container>
      </div>

      {/* Edit Project Modal */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Form.Group as={Col} controlId="formProjectId">
                <Form.Label>Project ID</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedProjectId}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                  required
                >
                  <option value="">Select a project</option>
                  {projects.map((project) => (
                    <option key={project.projectId} value={project.projectId}>
                      {project.projectId} - {project.prTitle}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="formTitle">
                <Form.Label>Project Title</Form.Label>
                <Form.Control
                  type="text"
                  name="prTitle"
                  value={projectData.prTitle}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group as={Col} controlId="formDescription">
                <Form.Label>Project Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="prDescription"
                  value={projectData.prDescription}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="formTags">
                <Form.Label>Project Tags</Form.Label>
                <Form.Control
                  type="text"
                  name="prTags"
                  value={projectData.prTags}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group as={Col} controlId="formClient">
                <Form.Label>Project Client</Form.Label>
                <Form.Control
                  type="text"
                  name="prClient"
                  value={projectData.prClient}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="formStartDate">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="prStartDate"
                  value={projectData.prStartDate}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group as={Col} controlId="formEndDate">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  name="prEndDate"
                  value={projectData.prEndDate}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="formType">
                <Form.Label>Project Type</Form.Label>
                <Form.Control
                  type="text"
                  name="prTypes"
                  value={projectData.prTypes}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group as={Col} controlId="formTeam">
                <Form.Label>Team Assigned</Form.Label>
                <Form.Control
                  type="text"
                  name="teamAssigned"
                  value={projectData.teamAssigned}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="formStatus">
                <Form.Label>Project Status</Form.Label>
                <Form.Control
                  type="text"
                  name="projectStatus"
                  value={projectData.projectStatus}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group as={Col} controlId="formCategory">
                <Form.Label>Project Category</Form.Label>
                <Form.Control
                  type="text"
                  name="projectCategory"
                  value={projectData.projectCategory}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="formComments">
                <Form.Label>Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="comments"
                  value={projectData.comments}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} controlId="formSalesperson">
                <Form.Label>Salesperson Code</Form.Label>
                <Form.Control
                  type="text"
                  name="salespersonCode"
                  value={projectData.salespersonCode}
                  onChange={handleInputChange}
                />
              </Form.Group>

              <Form.Group as={Col} controlId="formManager">
                <Form.Label>Project Manager</Form.Label>
                <Form.Control
                  type="text"
                  name="currentProjectManager"
                  value={projectData.currentProjectManager}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Row>

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={handleCloseEditModal}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isLoading}>
                {isLoading ? "Updating..." : "Update Project"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ViewProjects;

// import { useEffect, useState } from "react";
// import {
//   fetchProjects,
//   downloadProjectsExcel,
//   fetchProjectDetails,
//   updateProject,
//   fetchProjectsByOrg,
// } from "../Api/ViewProjects";
// import {
//   Container,
//   Table,
//   Button,
//   Modal,
//   Form,
//   Row,
//   Col,
// } from "react-bootstrap";
// import { toast } from "react-toastify";
// import moment from "moment";

// const ViewProjects = () => {
//   const [projects, setProjects] = useState([]);
//   const [isDownloading, setIsDownloading] = useState(false);
//   const [showEditModal, setShowEditModal] = useState(false);
//   const [selectedProjectId, setSelectedProjectId] = useState("");

//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = userDetails?.userId; // For API call
//   const orgId = userDetails?.organisationId; // For API call

//   const [projectData, setProjectData] = useState({
//     prTitle: "",
//     prDescription: "",
//     prTags: "",
//     prStartDate: "",
//     prEndDate: "",
//     prClient: "",
//     prTypes: "",
//     teamAssigned: "",
//     projectStatus: "",
//     projectCategory: "",
//     comments: "",
//     salespersonCode: "",
//     currentProjectManager: "",
//   });
//   const [isLoading, setIsLoading] = useState(false);

//   useEffect(() => {
//     const loadProjects = async () => {
//       try {
//         const data = await fetchProjectsByOrg(orgId);
//         console.log("Fetched Projects:", data);
//         setProjects(data || []);
//       } catch (error) {
//         toast.error(`🦄 Error Fetching Projects`);
//       }
//     };

//     loadProjects();
//   }, []);

//   const handleDownload = async () => {
//     setIsDownloading(true);
//     try {
//       const response = await downloadProjectsExcel();

//       if (!response.data) {
//         throw new Error("No data received from server");
//       }

//       // Create download link
//       const url = window.URL.createObjectURL(new Blob([response.data]));
//       const link = document.createElement("a");
//       link.href = url;
//       link.setAttribute("download", "Projects_Data.xlsx");

//       // Trigger download
//       document.body.appendChild(link);
//       link.click();

//       // Cleanup
//       setTimeout(() => {
//         window.URL.revokeObjectURL(url);
//         link.parentNode.removeChild(link);
//       }, 100);

//       toast.success("Projects data download started!");
//     } catch (error) {
//       console.error("Download error:", error);
//       toast.error(`🦄 Download failed: ${error.message}`);
//     } finally {
//       setIsDownloading(false);
//     }
//   };

//   const handleEditClick = () => {
//     setShowEditModal(true);
//   };

//   const handleCloseEditModal = () => {
//     setShowEditModal(false);
//     setSelectedProjectId("");
//     setProjectData({
//       prTitle: "",
//       prDescription: "",
//       prTags: "",
//       prStartDate: "",
//       prEndDate: "",
//       prClient: "",
//       prTypes: "",
//       teamAssigned: "",
//       projectStatus: "",
//       projectCategory: "",
//       comments: "",
//       salespersonCode: "",
//       currentProjectManager: "",
//     });
//   };

//   const handleProjectSelect = async (projectId) => {
//     setSelectedProjectId(projectId);
//     try {
//       setIsLoading(true);
//       const data = await fetchProjectDetails(projectId);
//       setProjectData({
//         prTitle: data.prTitle || "",
//         prDescription: data.prDescription || "",
//         prTags: data.prTags || "",
//         prStartDate: data.prStartDate
//           ? moment(data.prStartDate).format("YYYY-MM-DD")
//           : "",
//         prEndDate: data.prEndDate
//           ? moment(data.prEndDate).format("YYYY-MM-DD")
//           : "",
//         prClient: data.prClient || "",
//         prTypes: data.prTypes || "",
//         teamAssigned: data.teamAssigned || "",
//         projectStatus: data.projectStatus || "",
//         projectCategory: data.projectCategory || "",
//         comments: data.comments || "",
//         salespersonCode: data.salespersonCode || "",
//         currentProjectManager: data.currentProjectManager || "",
//       });
//     } catch (error) {
//       toast.error(`🦄 Error fetching project details`);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setProjectData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!selectedProjectId) {
//       toast.error("Please select a project");
//       return;
//     }

//     try {
//       setIsLoading(true);
//       await updateProject(selectedProjectId, projectData);
//       toast.success("Project updated successfully!");

//       // Refresh the projects list
//       const data = await fetchProjects();
//       setProjects(data || []);

//       handleCloseEditModal();
//     } catch (error) {
//       toast.error(`🦄 Error updating project: ${error.message}`);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <>
//       <div className="main-content expand">
//         <Container fluid className="list-container">
//           <div className="d-flex justify-content-between align-items-center mb-3">
//             <h2 className="list-title">View Projects</h2>
//             <div>
//               <Button
//                 variant="secondary"
//                 onClick={handleEditClick}
//                 disabled={projects.length === 0}
//                 className="me-2"
//               >
//                 Edit Projects
//               </Button>
//               <Button
//                 className="generate-btn"
//                 variant="primary"
//                 onClick={handleDownload}
//                 disabled={isDownloading || projects.length === 0}
//               >
//                 {isDownloading ? "Downloading..." : "Download Data"}
//               </Button>
//             </div>
//           </div>

//           {projects.length > 0 ? (
//             <div className="table-wrapper">
//               <div className="table-scroll-container">
//                 <Table bordered hover responsive className="data-table">
//                   {/* Table content remains the same */}
//                   <thead>
//                     <tr>
//                       <th>#</th>
//                       <th>Project ID</th>
//                       <th>Title</th>
//                       <th>Description</th>
//                       <th>Tags</th>
//                       <th>Start Date</th>
//                       <th>End Date</th>
//                       <th>Client</th>
//                       <th>Type</th>
//                       <th>User ID</th>
//                       <th>Team</th>
//                       <th>Status</th>
//                       <th>Category</th>
//                       <th>Comments</th>
//                       <th>Created On</th>
//                       <th>Created By</th>
//                       <th>Changed On</th>
//                       <th>Changed By</th>
//                       <th>Salesperson Code</th>
//                       <th>Project Manager</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {projects.map((project, index) => (
//                       <tr key={index}>
//                         <td>{index + 1}</td>
//                         <td>{project.projectId}</td>
//                         <td>{project.prTitle}</td>
//                         <td>{project.prDescription}</td>
//                         <td>{project.prTags}</td>
//                         <td>
//                           {project.prStartDate
//                             ? moment(project.prStartDate).format("L")
//                             : "N/A"}
//                         </td>
//                         <td>
//                           {project.prEndDate
//                             ? moment(project.prEndDate).format("L")
//                             : "N/A"}
//                         </td>
//                         <td>{project.prClient}</td>
//                         <td>{project.prTypes}</td>
//                         <td>{project.usersId}</td>
//                         <td>{project.teamAssigned}</td>
//                         <td>{project.projectStatus}</td>
//                         <td>{project.projectCategory}</td>
//                         <td>{project.comments}</td>
//                         <td>
//                           {project.createdOnDate
//                             ? moment(project.createdOnDate).format("L")
//                             : "N/A"}
//                           {project.createdOnTime &&
//                             ` at ${project.createdOnTime.split(".")[0]}`}
//                         </td>
//                         <td>{project.createdBy || "N/A"}</td>
//                         <td>
//                           {project.changedOnDate
//                             ? moment(project.changedOnDate).format("L")
//                             : "N/A"}
//                           {project.changedOnTime &&
//                             ` at ${project.changedOnTime.split(".")[0]}`}
//                         </td>
//                         <td>{project.changedBy}</td>
//                         <td>{project.salespersonCode}</td>
//                         <td>{project.currentProjectManager}</td>
//                       </tr>
//                     ))}
//                   </tbody>
//                 </Table>
//               </div>
//             </div>
//           ) : (
//             <p className="no-data">No projects found.</p>
//           )}
//         </Container>
//       </div>

//       {/* Edit Project Modal */}
//       <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
//         <Modal.Header closeButton>
//           <Modal.Title>Edit Project</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           <Form onSubmit={handleSubmit}>
//             <Row className="mb-3">
//               <Form.Group as={Col} controlId="formProjectId">
//                 <Form.Label>Project ID</Form.Label>
//                 <Form.Control
//                   as="select"
//                   value={selectedProjectId}
//                   onChange={(e) => handleProjectSelect(e.target.value)}
//                   required
//                 >
//                   <option value="">Select a project</option>
//                   {projects.map((project) => (
//                     <option key={project.prOrderId} value={project.prOrderId}>
//                       {project.prOrderId} - {project.prTitle}
//                     </option>
//                   ))}
//                 </Form.Control>
//               </Form.Group>
//             </Row>

//             <Row className="mb-3">
//               <Form.Group as={Col} controlId="formTitle">
//                 <Form.Label>Project Title</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="prTitle"
//                   value={projectData.prTitle}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>

//               <Form.Group as={Col} controlId="formDescription">
//                 <Form.Label>Project Description</Form.Label>
//                 <Form.Control
//                   as="textarea"
//                   rows={2}
//                   name="prDescription"
//                   value={projectData.prDescription}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>
//             </Row>

//             <Row className="mb-3">
//               <Form.Group as={Col} controlId="formTags">
//                 <Form.Label>Project Tags</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="prTags"
//                   value={projectData.prTags}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>

//               <Form.Group as={Col} controlId="formClient">
//                 <Form.Label>Project Client</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="prClient"
//                   value={projectData.prClient}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>
//             </Row>

//             <Row className="mb-3">
//               <Form.Group as={Col} controlId="formStartDate">
//                 <Form.Label>Start Date</Form.Label>
//                 <Form.Control
//                   type="date"
//                   name="prStartDate"
//                   value={projectData.prStartDate}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>

//               <Form.Group as={Col} controlId="formEndDate">
//                 <Form.Label>End Date</Form.Label>
//                 <Form.Control
//                   type="date"
//                   name="prEndDate"
//                   value={projectData.prEndDate}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>
//             </Row>

//             <Row className="mb-3">
//               <Form.Group as={Col} controlId="formType">
//                 <Form.Label>Project Type</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="prTypes"
//                   value={projectData.prTypes}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>

//               <Form.Group as={Col} controlId="formTeam">
//                 <Form.Label>Team Assigned</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="teamAssigned"
//                   value={projectData.teamAssigned}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>
//             </Row>

//             <Row className="mb-3">
//               <Form.Group as={Col} controlId="formStatus">
//                 <Form.Label>Project Status</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="projectStatus"
//                   value={projectData.projectStatus}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>

//               <Form.Group as={Col} controlId="formCategory">
//                 <Form.Label>Project Category</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="projectCategory"
//                   value={projectData.projectCategory}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>
//             </Row>

//             <Row className="mb-3">
//               <Form.Group as={Col} controlId="formComments">
//                 <Form.Label>Comments</Form.Label>
//                 <Form.Control
//                   as="textarea"
//                   rows={2}
//                   name="comments"
//                   value={projectData.comments}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>
//             </Row>

//             <Row className="mb-3">
//               <Form.Group as={Col} controlId="formSalesperson">
//                 <Form.Label>Salesperson Code</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="salespersonCode"
//                   value={projectData.salespersonCode}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>

//               <Form.Group as={Col} controlId="formManager">
//                 <Form.Label>Project Manager</Form.Label>
//                 <Form.Control
//                   type="text"
//                   name="currentProjectManager"
//                   value={projectData.currentProjectManager}
//                   onChange={handleInputChange}
//                 />
//               </Form.Group>
//             </Row>

//             <div className="d-flex justify-content-end">
//               <Button
//                 variant="secondary"
//                 onClick={handleCloseEditModal}
//                 className="me-2"
//               >
//                 Cancel
//               </Button>
//               <Button variant="primary" type="submit" disabled={isLoading}>
//                 {isLoading ? "Updating..." : "Update Project"}
//               </Button>
//             </div>
//           </Form>
//         </Modal.Body>
//       </Modal>
//     </>
//   );
// };

// export default ViewProjects;
